/**
 * The built-in dance styles, and how the site turns `danceStyles` rows into the
 * list it renders.
 *
 * Styles are admin-managed in the `danceStyles` table (Admin → Dance Styles).
 * This list is what the site shows while that table is empty or unreachable —
 * a fresh deployment, before anyone has pressed "Import defaults", or a share
 * image whose Convex call failed — and it is what "Import defaults" writes.
 *
 * Kept free of Convex and browser imports so the app (`src/lib/constants.ts`),
 * the Convex functions and the Vercel edge functions (`api/_lib/event.ts`) all
 * read the same list.
 */

export interface DanceStyle {
  name: string;
  slug: string;
  color: string;
  group: string;
}

export const DANCE_STYLE_GROUPS = ["Latin", "Swing", "Ballroom", "Country", "Other"] as const;

// Muted, desaturated colors that work well on dark backgrounds
export const DEFAULT_DANCE_STYLES: readonly DanceStyle[] = [
  // Latin
  { name: "Bachata", slug: "bachata", color: "#E07A9E", group: "Latin" },
  { name: "Salsa", slug: "salsa", color: "#E08A6D", group: "Latin" },
  { name: "Merengue", slug: "merengue", color: "#E8B84B", group: "Latin" },
  { name: "Kizomba", slug: "kizomba", color: "#B088C4", group: "Latin" },
  { name: "Zouk", slug: "zouk", color: "#5ABFBF", group: "Latin" },
  { name: "Argentine Tango", slug: "argentine-tango", color: "#D97070", group: "Latin" },
  // Swing
  { name: "Lindy Hop", slug: "lindy-hop", color: "#7BBF6A", group: "Swing" },
  { name: "East Coast Swing", slug: "east-coast-swing", color: "#5C9ED6", group: "Swing" },
  { name: "West Coast Swing", slug: "west-coast-swing", color: "#6BA3D6", group: "Swing" },
  { name: "Balboa", slug: "balboa", color: "#8FBC8F", group: "Swing" },
  { name: "Blues", slug: "blues", color: "#5B7FA6", group: "Swing" },
  { name: "Hustle", slug: "hustle", color: "#D4B86A", group: "Swing" },
  // Ballroom
  { name: "Ballroom", slug: "ballroom", color: "#D6A45C", group: "Ballroom" },
  // Country
  { name: "Country Two-Step", slug: "two-step", color: "#A68B6D", group: "Country" },
  { name: "Square / Line Dancing", slug: "square-line-dancing", color: "#C4A27A", group: "Country" },
  // Other
  { name: "Vintage / Historical", slug: "vintage-historical", color: "#8B7D6B", group: "Other" },
];

/** A `danceStyles` row, as far as rendering cares. */
export interface DanceStyleRow {
  name: string;
  slug: string;
  color: string;
  group?: string;
  sortOrder?: number;
}

/** A `danceStyleCategories` row. */
export interface DanceStyleCategoryRow {
  name: string;
  sortOrder: number;
}

/** An event can star up to this many of its styles as its main ones. */
export const MAX_MAIN_STYLES = 3;

export interface ResolvedDanceStyles {
  /** Every style in display order: category by category, then within each. */
  styles: DanceStyle[];
  styleMap: Record<string, DanceStyle>;
  /** Categories that have at least one style, in display order. */
  groups: string[];
  /** Every category in display order, including empty ones. */
  categories: string[];
  /** Each slug's position in `styles`, for sorting an event's styles. */
  rank: Record<string, number>;
  /** True when the rows were empty or missing and the defaults are shown. */
  usingFallback: boolean;
}

/**
 * The list the site renders: the table's rows when it has any, the defaults
 * otherwise. Categories come from their own table in the order an admin
 * dragged them into; until that has rows, the built-in category order is used.
 * A style whose category is missing lands in "Other".
 */
export function resolveDanceStyles(
  rows: readonly DanceStyleRow[] | null | undefined,
  categoryRows?: readonly DanceStyleCategoryRow[] | null
): ResolvedDanceStyles {
  const usingFallback = !rows || rows.length === 0;
  const unsorted: DanceStyle[] = usingFallback
    ? DEFAULT_DANCE_STYLES.map((s) => ({ ...s }))
    : [...rows]
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .map((r) => ({ name: r.name, slug: r.slug, color: r.color, group: r.group || "Other" }));

  // Category order: the admin's, else the built-in one; then any category a
  // style names that isn't listed, in the order styles mention it.
  const listed =
    !usingFallback && categoryRows && categoryRows.length > 0
      ? [...categoryRows].sort((a, b) => a.sortOrder - b.sortOrder).map((c) => c.name)
      : [...DANCE_STYLE_GROUPS];
  const categories = [...listed];
  for (const style of unsorted) {
    if (!categories.includes(style.group)) categories.push(style.group);
  }

  // Stable sort by category, keeping each style's own order within it.
  const categoryIndex = new Map(categories.map((c, i) => [c, i]));
  const styles = unsorted
    .map((style, i) => ({ style, i }))
    .sort(
      (a, b) =>
        categoryIndex.get(a.style.group)! - categoryIndex.get(b.style.group)! || a.i - b.i
    )
    .map(({ style }) => style);

  const styleMap: Record<string, DanceStyle> = {};
  const rank: Record<string, number> = {};
  styles.forEach((style, i) => {
    styleMap[style.slug] = style;
    rank[style.slug] = i;
  });

  const present = new Set(styles.map((s) => s.group));
  const groups = categories.filter((c) => present.has(c));

  return { styles, styleMap, groups, categories, rank, usingFallback };
}

/**
 * An event's main styles, cleaned: only styles the event actually has, no
 * repeats, at most `MAX_MAIN_STYLES`. Used on every write so a stale or
 * hand-crafted value can't lead with a style the event doesn't carry.
 */
export function cleanMainStyles(
  mainStyles: readonly string[] | undefined,
  danceStyles: readonly string[]
): string[] {
  const allowed = new Set(danceStyles);
  return [...new Set(mainStyles ?? [])].filter((s) => allowed.has(s)).slice(0, MAX_MAIN_STYLES);
}

/**
 * The order an event's styles are shown in — badges, share tags, and the
 * colour that stands for the event (the first one). Main styles lead, then
 * everything else in the site's list order. Slugs the site doesn't know go
 * last, in the order the event has them.
 */
export function orderEventStyles(
  danceStyles: readonly string[] | undefined,
  mainStyles: readonly string[] | undefined,
  rank: Record<string, number>
): string[] {
  const all = [...new Set(danceStyles ?? [])];
  const main = new Set(cleanMainStyles(mainStyles, all));
  const position = (slug: string) => rank[slug] ?? Number.MAX_SAFE_INTEGER;
  return all
    .map((slug, i) => ({ slug, i }))
    .sort(
      (a, b) =>
        Number(main.has(b.slug)) - Number(main.has(a.slug)) ||
        position(a.slug) - position(b.slug) ||
        a.i - b.i
    )
    .map(({ slug }) => slug);
}
